import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Empty } from "../components/common";
import { PartnerBriefCard } from "../components/PartnerBriefCard";
import { PartnerWrapCard } from "../components/PartnerWrapCard";
import { WeeklyInsightsCard } from "../components/WeeklyInsightsCard";
import { BriefInsights } from "../components/BriefInsights";

const Stat = ({ label, value, tid, tone = "" }) => (value === undefined ? null :
  <div className="border p-3" data-testid={tid}><div className="text-xs text-muted-foreground">{label}</div><div className={`num text-2xl mt-1 ${tone}`}>{value}</div></div>
);
const pctTone = (v) => {
  if (v == null) return "";
  if (v >= 80) return "text-emerald-400";
  return v >= 50 ? "text-amber-400" : "text-red-400";
};
const hm = (s) => new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", timeStyle: "short", dateStyle: "medium" });

const Section = ({ title, children }) => (
  <div className="ax-panel p-4 mb-4"><div className="ax-label mb-3">{title}</div>{children}</div>
);

const Who = ({ title, rows, tid }) => (!rows?.length ? null : (
  <div className="mt-3" data-testid={tid}><div className="text-xs text-muted-foreground mb-1">{title}</div>
    <div className="flex flex-wrap gap-1.5">{rows.map((r) => <span key={r.name} className="border px-2 py-0.5 text-xs"><span className="font-medium">{r.name}</span> <span className="num text-rose-500">{r.n}</span></span>)}</div></div>));

const CallbackTable = ({ c }) => (!c.rows.length ? <p className="text-sm text-muted-foreground" data-testid="brief-callbacks-empty">No callbacks were due today.</p> : (
  <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="brief-callbacks-table">
    <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Agent</th><th className="pr-2">On time</th><th className="pr-2">Late</th><th className="pr-2">Missed</th><th className="pr-2">Open</th><th>%</th></tr></thead>
    <tbody>{c.rows.map((r) => <tr key={r.user_id} className="border-t border-border/50"><td className="py-2 pr-2 font-medium">{r.agent}</td><td className="num pr-2 text-emerald-400">{r.on_time}</td>
      <td className="num pr-2 text-amber-400">{r.late}</td><td className="num pr-2 text-red-400">{r.missed}</td><td className="num pr-2">{r.open}</td>
      <td className={`num font-semibold ${pctTone(r.on_time_pct)}`}>{r.on_time_pct == null ? "—" : `${r.on_time_pct}%`}</td></tr>)}</tbody>
  </table></div>));

const Numbers = ({ b }) => (<>
  <Section title="Orders & delivery"><div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
    <Stat label="Past delivery date" tid="brief-late" value={b.orders.past_committed} tone="text-red-400" /><Stat label="Delivery due in 7 days" tid="brief-due7" value={b.orders.due_next_7_days} tone="text-amber-500" />
    <Stat label="POs without delivery date" tid="brief-no-delivery" value={b.orders.po_without_delivery_date} /><Stat label="Pre-PO past planned PO date" tid="brief-prepo-late" value={b.orders.prepo_past_po_date} />
    <Stat label="Open UIDs, no next action" tid="brief-no-next" value={b.orders.no_next_action} /><Stat label="New UIDs today" tid="brief-new-uids" value={b.orders.new_uids} />
    <Stat label="POs booked today" tid="brief-pos" value={b.orders.pos_booked} /><Stat label="PO value today" tid="brief-po-value" value={b.orders.po_value} />
    <Stat label="POs this month" tid="brief-month-pos" value={b.orders.month_pos} /><Stat label="PO value this month" tid="brief-month-value" value={b.orders.month_po_value} />
    <Stat label="Open Pre-PO · value" tid="brief-pre-po" value={`${b.orders.open_pre_po} · ${b.orders.open_pre_po_value}`} /></div>
    <Who title="Most UIDs past delivery date" rows={b.orders.late_by_owner} tid="brief-late-owners" /></Section>
  <Section title="Sales"><div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
    <Stat label="New leads today" tid="brief-new-leads" value={b.sales.new_leads} /><Stat label="Overdue follow-ups" tid="brief-overdue-fu" value={b.sales.overdue_followups} tone="text-red-400" />
    <Stat label="Open leads, no next date" tid="brief-no-date" value={b.sales.no_next_date} /><Stat label="Open RFQs (D)" tid="brief-open-rfqs" value={b.sales.open_rfqs} />
    <Stat label="Opened a file (24h)" tid="brief-hot" value={b.sales.hot_leads} /></div>
    <Who title="Most overdue follow-ups" rows={b.sales.overdue_by_owner} tid="brief-overdue-owners" /></Section>
  {b.ops && <Section title="Production"><div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
    <Stat label="Steps past fixed deadline" tid="brief-ops-late" value={b.ops.late_steps} tone="text-red-400" /><Stat label="Blocked steps" tid="brief-ops-blocked" value={b.ops.blocked_steps} tone="text-amber-500" />
    <Stat label="Open steps without owner" tid="brief-ops-noowner" value={b.ops.steps_without_owner} /></div>
    <Who title="Most late steps" rows={b.ops.late_steps_by_owner} tid="brief-ops-owners" /></Section>}
  <Section title="Team callbacks today">
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
      <Stat label="On-time %" tid="brief-cb-pct" value={b.callbacks.on_time_pct == null ? "—" : `${b.callbacks.on_time_pct}%`} tone={pctTone(b.callbacks.on_time_pct)} />
      <Stat label="On time" tid="brief-cb-ontime" value={b.callbacks.on_time} /><Stat label="Late" tid="brief-cb-late" value={b.callbacks.late} />
      <Stat label="Missed" tid="brief-cb-missed" value={b.callbacks.missed} /><Stat label="Still open" tid="brief-cb-open" value={b.callbacks.open} />
    </div>
    <CallbackTable c={b.callbacks} />
  </Section>
</>);

const useLive = (on) => {
  const [live, setLive] = useState(null);
  const load = useCallback(() => api.get("/brief/live").then((r) => setLive(r.data.live)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => {
    if (!on) return undefined;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [on, load]);
  return [live, load];
};

export default function Brief() {
  const { user } = useAuth();
  return user?.role_key === "founder" ? <BriefPage /> : <div className="p-6" data-testid="brief-no-access"><Empty text="The Founders Brief is for founders only." /></div>;
}

function BriefPage() {
  const [sp, setSp] = useSearchParams();
  const date = sp.get("date");
  const [past, setPast] = useState(null);
  const [live, reload] = useLive(!date);
  useEffect(() => { api.get("/brief", { params: date ? { date } : {} }).then((r) => setPast(r.data)).catch(() => {}); }, [date]);
  const b = date ? past?.brief : live;
  return (
    <div>
      <PageHeader eyebrow="Founders only" title="Founders brief" desc="Live numbers that refresh every minute, with AI insights on what to focus on. Values in ₹ Lakhs, before GST. A copy is saved every evening at 5:35 PM." />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="brief-date-select" value={date || ""} onChange={(e) => setSp(e.target.value ? { date: e.target.value } : {})} className="bg-muted/60 border h-9 px-2 text-sm">
          <option value="">Live now</option>
          {(past?.dates || []).map((x) => <option key={x} value={x}>Saved · {new Date(`${x}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</option>)}</select>
        {!date && <button data-testid="brief-refresh" onClick={reload} className="border border-primary text-primary h-9 px-4 text-sm flex items-center gap-1.5"><RefreshCw className="h-4 w-4" />Refresh now</button>}
        {!date && live && <span className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="brief-live-at"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />Live · updated {hm(live.live_at)}</span>}
        {date && b && <span className="text-xs text-muted-foreground" data-testid="brief-generated-at">Saved brief for {b.date} · built {hm(b.generated_at)}</span>}
      </div>
      {!date && <BriefInsights />}
      {!b ? (date ? <Empty text="No saved brief for that day." /> : <div className="text-sm text-muted-foreground">Loading live numbers…</div>) : <Numbers b={b} />}
      <PartnerBriefCard />
      <PartnerWrapCard />
      <WeeklyInsightsCard />
    </div>
  );
}
